import type { AccessPermission, AccessRole } from '@ferty9/types';
import { useMemo, useState, type CSSProperties } from 'react';
import type { AdminActions } from '../types';

export function RoleManagementTab({
  active,
  roles,
  permissions,
  actions,
}: {
  active: boolean;
  roles: AccessRole[];
  permissions: AccessPermission[];
  actions: AdminActions;
}) {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rank, setRank] = useState(10);
  const [colorHex, setColorHex] = useState('#7c3aed');
  const [colorRgba, setColorRgba] = useState('rgba(124, 58, 237, 0.12)');
  const [colorText, setColorText] = useState('#7c3aed');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const permissionSections = useMemo(() => {
    const byModule = permissions.reduce<Record<string, AccessPermission[]>>((acc, permission) => {
      const moduleName = permission.module_key || 'general';
      acc[moduleName] ||= [];
      acc[moduleName].push(permission);
      return acc;
    }, {});

    Object.values(byModule).forEach(modulePermissions => {
      modulePermissions.sort((left, right) => permissionSortValue(left).localeCompare(permissionSortValue(right)));
    });

    const consumedModules = new Set<string>();
    const sections: PermissionSection[] = permissionSectionDefinitions
      .map(section => {
        const modules = section.modules.reduce<PermissionModuleGroup[]>((items, moduleKey) => {
            const modulePermissions = byModule[moduleKey] || [];
            if (!modulePermissions.length) return items;
            consumedModules.add(moduleKey);
            items.push({
              key: moduleKey,
              label: permissionModuleLabels[moduleKey] || titleFromKey(moduleKey),
              permissions: modulePermissions,
            });
            return items;
          }, []);
        return { ...section, modules };
      })
      .filter(section => section.modules.length > 0);

    const otherModules = Object.entries(byModule)
      .filter(([moduleKey]) => !consumedModules.has(moduleKey))
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([moduleKey, modulePermissions]) => ({
        key: moduleKey,
        label: permissionModuleLabels[moduleKey] || titleFromKey(moduleKey),
        permissions: modulePermissions,
      }));

    if (otherModules.length) {
      sections.push({
        key: 'other',
        label: 'Other Permissions',
        description: 'Additional permissions not mapped to a primary app area yet.',
        modules: otherModules,
      });
    }

    return sections;
  }, [permissions]);

  const selectedRole = roles.find(role => role.key === editingKey);
  const editingSystemRole = Boolean(selectedRole?.is_system || selectedRole?.is_root_only);
  const displayedPermissions = selectedRole?.is_root_only
    ? permissions.map(permission => permission.key)
    : selectedPermissions;

  const reset = () => {
    setEditingKey(null);
    setName('');
    setDescription('');
    setRank(10);
    setColorHex('#7c3aed');
    setColorRgba('rgba(124, 58, 237, 0.12)');
    setColorText('#7c3aed');
    setSelectedPermissions([]);
  };

  const startCreate = () => {
    reset();
  };

  const startEdit = (role: AccessRole) => {
    setEditingKey(role.key);
    setName(role.name);
    setDescription(role.description || '');
    setRank(role.rank ?? 10);
    setColorHex(role.color_hex || '#7c3aed');
    setColorRgba(role.color_rgba || rgbaFromHex(role.color_hex || '#7c3aed'));
    setColorText(role.color_text || role.color_hex || '#7c3aed');
    setSelectedPermissions(permissionKeysForRole(role, permissions));
  };

  const startClone = (role: AccessRole) => {
    setEditingKey(null);
    setName(`${role.name} Copy`);
    setDescription(role.description || '');
    setRank(Math.max(1, Math.min(79, (role.rank ?? 20) - 1)));
    setColorHex(role.color_hex || '#7c3aed');
    setColorRgba(role.color_rgba || rgbaFromHex(role.color_hex || '#7c3aed'));
    setColorText(role.color_text || role.color_hex || '#7c3aed');
    setSelectedPermissions(permissionKeysForRole(role, permissions));
  };

  const togglePermission = (permissionKey: string) => {
    setSelectedPermissions(previous =>
      previous.includes(permissionKey)
        ? previous.filter(item => item !== permissionKey)
        : [...previous, permissionKey].sort(),
    );
  };

  const submit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      actions.notify('Role name is required.');
      return;
    }
    if (!selectedPermissions.length) {
      actions.notify('Select at least one permission.');
      return;
    }
    const payload = {
      name: trimmedName,
      description: description.trim(),
      permissionKeys: selectedPermissions,
      is_active: true,
      rank,
      color_hex: colorHex,
      color_rgba: colorRgba || rgbaFromHex(colorHex),
      color_text: colorText || colorHex,
      color_preset_key: 'custom',
    };
    if (editingKey) await actions.updateRole(editingKey, payload);
    else await actions.createRole(payload);
    reset();
  };

  return (
    <section id="tab-roles" className="tab-content" style={{ display: active ? 'block' : 'none' }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
          <div>
            <h2 className="section-title" style={{ margin: 0, fontFamily: 'Outfit', fontSize: 28, fontWeight: 800, color: 'var(--text)' }}>Role Management</h2>
            <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontWeight: 600 }}>
              Create custom roles, clone protected system roles, and assign permissions by module.
            </p>
          </div>
          <button className="btn btn-primary" onClick={startCreate}>+ Create Custom Role</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 0.8fr) minmax(360px, 1.2fr)', gap: 24 }}>
          <div style={{ border: '1px solid var(--border)', borderRadius: 18, overflow: 'hidden', background: 'var(--surface-solid)' }}>
            <div style={{ padding: 18, borderBottom: '1px solid var(--border)', fontWeight: 900, fontFamily: 'Outfit' }}>Available Roles</div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {roles.map(role => {
                const protectedRole = role.is_system || role.is_root_only;
                return (
                  <div key={role.key} style={{ padding: 16, borderBottom: '1px solid var(--border)', display: 'grid', gap: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <div>
                        <div style={{ fontWeight: 900, color: 'var(--text)', fontFamily: 'Outfit' }}>{role.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700 }}>{role.key}</div>
                      </div>
                      <span className="badge" style={{ background: protectedRole ? '#fef3c7' : '#dcfce7', color: protectedRole ? '#92400e' : '#166534', height: 24 }}>
                        {protectedRole ? 'Protected' : 'Custom'}
                      </span>
                    </div>
                    {role.description && <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.5 }}>{role.description}</p>}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button className="btn btn-outline" style={{ height: 36, padding: '0 14px', fontSize: 12 }} onClick={() => startEdit(role)}>View{protectedRole ? '' : ' / Edit'}</button>
                      <button className="btn btn-outline" style={{ height: 36, padding: '0 14px', fontSize: 12 }} onClick={() => startClone(role)}>Clone</button>
                      {!protectedRole && <button className="btn btn-outline" style={{ height: 36, padding: '0 14px', fontSize: 12, color: 'var(--danger)', borderColor: 'rgba(239, 68, 68, 0.25)' }} onClick={() => void actions.deleteRole(role.key)}>Delete</button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 18, background: 'var(--surface-solid)', padding: 22 }}>
            <h3 style={{ margin: '0 0 18px', fontFamily: 'Outfit', fontSize: 20, fontWeight: 900 }}>{editingKey ? `Role: ${selectedRole?.name || editingKey}` : 'Create Custom Role'}</h3>
            {editingSystemRole && (
              <div style={{ padding: 14, borderRadius: 14, background: '#fff7ed', color: '#9a3412', fontWeight: 700, marginBottom: 16 }}>
                System roles are RBAC-managed but protected from direct editing. Use Clone to create an editable custom role.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 14, marginBottom: 18 }}>
              <label style={{ display: 'grid', gap: 8, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: 11 }}>
                Role Name
                <input value={name} disabled={editingSystemRole} onChange={event => setName(event.target.value)} style={inputStyle} />
              </label>
              <label style={{ display: 'grid', gap: 8, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: 11 }}>
                Description
                <textarea value={description} disabled={editingSystemRole} onChange={event => setDescription(event.target.value)} rows={3} style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                <label style={{ display: 'grid', gap: 8, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: 11 }}>
                  Role Rank
                  <input type="number" min={1} max={99} value={rank} disabled={editingSystemRole} onChange={event => setRank(Number(event.target.value) || 10)} style={inputStyle} />
                </label>
                <label style={{ display: 'grid', gap: 8, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: 11 }}>
                  Role Color
                  <input type="color" value={colorHex} disabled={editingSystemRole} onChange={event => { setColorHex(event.target.value); setColorText(event.target.value); setColorRgba(rgbaFromHex(event.target.value)); }} style={{ ...inputStyle, padding: 6 }} />
                </label>
                <label style={{ display: 'grid', gap: 8, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: 11 }}>
                  Hex
                  <input value={colorHex} disabled={editingSystemRole} onChange={event => setColorHex(event.target.value)} style={inputStyle} />
                </label>
                <label style={{ display: 'grid', gap: 8, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: 11 }}>
                  RGBA
                  <input value={colorRgba} disabled={editingSystemRole} onChange={event => setColorRgba(event.target.value)} style={inputStyle} />
                </label>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {themeColorSuggestions.map(color => (
                  <button key={color.hex} type="button" disabled={editingSystemRole} onClick={() => { setColorHex(color.hex); setColorText(color.hex); setColorRgba(color.rgba); }} title={`${color.name}: ${color.hex} / ${color.rgba}`} style={{ border: color.hex === colorHex ? '2px solid var(--text)' : '1px solid var(--border)', borderRadius: 999, padding: '7px 12px', background: color.rgba, color: color.hex, fontWeight: 800, cursor: editingSystemRole ? 'not-allowed' : 'pointer' }}>
                    {color.name}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gap: 18, maxHeight: 520, overflowY: 'auto', paddingRight: 6 }}>
              {permissionSections.map(section => (
                <div key={section.key} style={{ border: '1px solid var(--border)', borderRadius: 18, padding: 16, background: 'var(--bg)' }}>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontWeight: 900, fontFamily: 'Outfit', color: 'var(--text)', fontSize: 18 }}>{section.label}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: 12, fontWeight: 650, marginTop: 4 }}>{section.description}</div>
                  </div>
                  <div style={{ display: 'grid', gap: 14 }}>
                    {section.modules.map(module => (
                      <div key={module.key} style={{ border: '1px solid var(--border)', borderRadius: 16, padding: 14, background: 'var(--surface-solid)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ fontWeight: 900, fontFamily: 'Outfit', color: 'var(--text)' }}>{module.label}</div>
                            <div style={{ color: 'var(--text-muted)', fontSize: 11, fontWeight: 700 }}>{module.key}</div>
                          </div>
                          <span className="badge" style={{ background: 'rgba(233, 30, 99, 0.10)', color: 'var(--primary)', height: 24 }}>
                            {module.permissions.length} permission{module.permissions.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                          {module.permissions.map(permission => (
                            <label key={permission.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: 10, borderRadius: 12, background: displayedPermissions.includes(permission.key) ? 'rgba(13, 148, 136, 0.08)' : 'var(--bg)', border: '1px solid var(--border)', cursor: editingSystemRole ? 'not-allowed' : 'pointer' }}>
                              <input type="checkbox" checked={displayedPermissions.includes(permission.key)} disabled={editingSystemRole} onChange={() => togglePermission(permission.key)} />
                              <span>
                                <span style={{ display: 'block', fontWeight: 800, color: 'var(--text)', fontSize: 13 }}>{permission.name}</span>
                                <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 11, marginTop: 3 }}>{permission.key}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 22, borderTop: '1px solid var(--border)', paddingTop: 18 }}>
              <button className="btn btn-outline" onClick={reset}>Cancel</button>
              {!editingSystemRole && <button className="btn btn-primary" onClick={() => void submit()}>{editingKey ? 'Save Role' : 'Create Role'}</button>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const inputStyle: CSSProperties = {
  minHeight: 46,
  borderRadius: 12,
  border: '1px solid var(--border)',
  background: 'var(--bg)',
  color: 'var(--text)',
  padding: '10px 14px',
  fontWeight: 650,
};

interface PermissionModuleGroup {
  key: string;
  label: string;
  permissions: AccessPermission[];
}

interface PermissionSection {
  key: string;
  label: string;
  description: string;
  modules: PermissionModuleGroup[];
}

const permissionSectionDefinitions = [
  {
    key: 'app-access',
    label: 'App Access',
    description: 'Top-level switches that decide whether the role can enter the Admin app or Survey app.',
    modules: ['admin_app', 'survey_app'],
  },
  {
    key: 'dashboard-reports',
    label: 'Dashboard and Reports',
    description: 'Sidebar page access for dashboard, tracker, response, and summary screens.',
    modules: ['dashboard', 'feedback_tracker', 'feedback_responses', 'question_wise_response', 'monthly_summary', 'detailed_summary'],
  },
  {
    key: 'administration',
    label: 'Administration',
    description: 'Operational admin pages and settings. Read permissions control page visibility; action permissions control changes.',
    modules: ['staff_management', 'user_accounts', 'survey_questions', 'role_management', 'general_settings', 'integration_settings', 'system_settings'],
  },
  {
    key: 'exports',
    label: 'Exports',
    description: 'Shared export center permissions.',
    modules: ['exports'],
  },
] as const;

const permissionModuleLabels: Record<string, string> = {
  admin_app: 'Admin App Access',
  survey_app: 'Survey App Access',
  dashboard: 'Dashboard',
  feedback_tracker: 'Feedback Tracker',
  clinical_dashboard: 'Clinical Dashboard',
  feedback_responses: 'Feedback Responses',
  question_wise_response: 'Question Responses',
  monthly_summary: 'Monthly Summary',
  detailed_summary: 'Detailed Summary',
  staff_management: 'Staff Management',
  user_accounts: 'User Accounts',
  survey_questions: 'Survey Questions',
  role_management: 'Role Management',
  general_settings: 'General Settings',
  integration_settings: 'Integration Settings',
  system_settings: 'System Settings',
  exports: 'Exports',
};

const permissionActionRank: Record<string, string> = {
  access: '00',
  use: '01',
  read: '02',
  create: '03',
  update: '04',
  delete: '05',
  export: '06',
  download: '07',
  manual: '08',
  send_message: '09',
  assign: '10',
  callback: '11',
  escalate: '12',
  audit: '13',
  manage: '14',
  manage_workflows: '15',
  rotate_secret: '16',
  test_connection: '17',
};

function permissionSortValue(permission: AccessPermission): string {
  return `${permissionActionRank[permission.action] || '99'}:${permission.key}`;
}

export function permissionKeysForRole(role: AccessRole, permissions: AccessPermission[]): string[] {
  if (role.is_root_only) return permissions.map(permission => permission.key).sort();
  const available = new Set(permissions.map(permission => permission.key));
  return (role.permissions || []).filter(key => available.has(key)).sort();
}

function titleFromKey(value: string): string {
  return value
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

const themeColorSuggestions = [
  { name: 'Ferty Pink', hex: '#e91e63', rgba: 'rgba(233, 30, 99, 0.12)' },
  { name: 'Rose', hex: '#e11d48', rgba: 'rgba(225, 29, 72, 0.12)' },
  { name: 'Teal', hex: '#0d9488', rgba: 'rgba(13, 148, 136, 0.12)' },
  { name: 'Blue', hex: '#2563eb', rgba: 'rgba(37, 99, 235, 0.12)' },
  { name: 'Violet', hex: '#7c3aed', rgba: 'rgba(124, 58, 237, 0.12)' },
  { name: 'Amber', hex: '#d97706', rgba: 'rgba(217, 119, 6, 0.14)' },
];

function rgbaFromHex(hex: string, alpha = 0.12): string {
  const normalized = hex.trim().replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return `rgba(124, 58, 237, ${alpha})`;
  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
